import {
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  Spacer,
  VStack,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { InputControl } from "formik-chakra-ui";
import React, { useCallback, useMemo } from "react";
import { RiAddCircleLine, RiDeleteBin6Line } from "react-icons/ri";
import { NavLink } from "react-router-dom";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { generateKey } from "src/scenes/orgAdmin/formTemplates/ContentTab/V2/autoGenerateKey";
import { validateWithZod } from "src/services/formValidations";
import * as Url from "src/services/url";
import { OrganizationSelect } from "../components/OrganizationSelect";
import {
  programFormInputToProgramType,
  ProgramFormType,
  ProgramGroupFormSchema,
  ProgramGroupFormType,
  ProgramGroupType,
  programToProgramFormInputType,
} from "./types/program";

interface ProgramGroupFormProps {
  initialProgramGroup?: ProgramGroupType;
  onSubmit: (programGroup: ProgramGroupType) => Promise<void>;
  submitting: boolean;
}

const emptyProgram: ProgramFormType = {
  label: "",
  key: "",
  order: "",
  userGeneratedKey: false,
};

export const ProgramGroupForm: React.FC<ProgramGroupFormProps> = ({
  initialProgramGroup,
  onSubmit,
  submitting,
}) => {
  const initialValues: ProgramGroupFormType = useMemo(
    () => ({
      organizationId: initialProgramGroup?.organization_id ?? "",
      name: initialProgramGroup?.name ?? "",
      programs: initialProgramGroup?.programs.map((program) =>
        programToProgramFormInputType(program)
      ) ?? [emptyProgram],
      programsToDeleteIds: [],
    }),
    [initialProgramGroup]
  );

  const handleSubmit = useCallback(
    async (values: ProgramGroupFormType) => {
      await onSubmit({
        organization_id: values.organizationId,
        name: values.name,
        programs: values.programs.map((program) =>
          programFormInputToProgramType(program)
        ),
        programs_to_delete_ids: values.programsToDeleteIds,
      });
    },
    [onSubmit]
  );

  return (
    <Formik<ProgramGroupFormType>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validateWithZod(ProgramGroupFormSchema)}
      enableReinitialize
    >
      {(formik) => (
        <Flex direction="column" minHeight="100%" as={Form} noValidate>
          <VStack align="left" spacing={8}>
            <Heading as="h1" variant="admin">
              {initialProgramGroup
                ? "Edit program group"
                : "Create program group"}
            </Heading>
            <Flex gap="4">
              <OrganizationSelect
                organizationId={formik.values.organizationId}
                onChange={(id) => {
                  formik.setFieldValue("organizationId", id ?? "");
                }}
                disabled={initialProgramGroup !== undefined}
                error={
                  formik.submitCount > 0
                    ? formik.errors.organizationId
                    : undefined
                }
              />
              <InputControl
                name="name"
                label="Program group"
                labelProps={{ requiredIndicator: <></> }}
                isRequired
              />
            </Flex>
            <Heading as="h2" size="md" mb={2}>
              Program configuration
            </Heading>
            <VStack gap={4}>
              {formik.values.programs.map((program, index) => (
                <HStack
                  width="100%"
                  align="end"
                  key={index}
                  gap={2}
                  alignItems="flex-start"
                >
                  <InputControl
                    name={`programs.${index}.label`}
                    label="Program label"
                    labelProps={{ requiredIndicator: <></> }}
                    isRequired
                    inputProps={{
                      onChange: async (event) => {
                        const label = event.target.value;
                        const keyName = `programs.${index}.key`;
                        const shouldChangeKey =
                          formik.values.programs[index]?.userGeneratedKey ===
                          false;

                        await formik.setFieldValue(
                          `programs.${index}.label`,
                          label,
                          false
                        );
                        if (shouldChangeKey) {
                          await formik.setFieldValue(
                            keyName,
                            generateKey(label),
                            false
                          );
                        }
                      },
                    }}
                  >
                    <Input type="text" />
                  </InputControl>
                  <InputControl
                    name={`programs.${index}.key`}
                    label="Key"
                    labelProps={{ requiredIndicator: <></> }}
                    isRequired
                    inputProps={{
                      onChange: async (event) => {
                        const key = event.target.value;
                        const userGeneratedKey = key.trim().length > 0;
                        await formik.setFieldValue(
                          `programs.${index}.key`,
                          key,
                          false
                        );
                        await formik.setFieldValue(
                          `programs.${index}.userGeneratedKey`,
                          userGeneratedKey,
                          false
                        );
                      },
                    }}
                  >
                    <Input type="text" />
                  </InputControl>
                  <InputControl
                    name={`programs.${index}.order`}
                    label="Order"
                    labelProps={{ requiredIndicator: <></> }}
                    isRequired
                  >
                    <Input type="number" />
                  </InputControl>
                  <IconButton
                    marginTop="1.8rem"
                    type="button"
                    aria-label="Remove program"
                    icon={<RiDeleteBin6Line />}
                    onClick={() => {
                      const newPrograms = [...formik.values.programs];
                      newPrograms.splice(index, 1);
                      formik.setFieldValue("programs", newPrograms);

                      if (program.id) {
                        formik.setFieldValue("programsToDeleteIds", [
                          ...formik.values.programsToDeleteIds,
                          program.id,
                        ]);
                      }
                    }}
                    variant="outline"
                  />
                </HStack>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                colorScheme="gray"
                aria-label="Add program"
                leftIcon={<RiAddCircleLine />}
                alignSelf="flex-end"
                onClick={() => {
                  formik.setFieldValue("programs", [
                    ...formik.values.programs,
                    emptyProgram,
                  ]);
                }}
              >
                Add program
              </Button>
            </VStack>
          </VStack>
          <Spacer minH={8} />
          <AdminFormButtons justifyContent="space-between">
            <Button
              as={NavLink}
              to={Url.Admin.Programs.index()}
              variant="outline"
              colorScheme="gray"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              marginLeft={4}
              isLoading={submitting}
              isDisabled={!formik.dirty}
            >
              Submit
            </Button>
          </AdminFormButtons>
        </Flex>
      )}
    </Formik>
  );
};
